import React from 'react';
import { Link } from './Link';
import type { SrpTopRolesStripItem } from '../types/srp-top-roles-strip';
import { salaryHubUrlPath, salaryRoleUrlPath } from '../utils/routing/salary-url-path';
import { trackEvent } from '../utils/analytics/analytics';

/**
 * Same 768px threshold as the mobile/desktop in-feed slots (see spliceResultsInterstitial):
 * approximates which of the two CSS-gated copies the visitor is actually seeing.
 */
function currentStripPlacement(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
  return window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
}

export interface SrpTopRolesStripLabels {
  label: string;
  hubCta: string;
}

export interface SrpTopRolesStripProps {
  roles: SrpTopRolesStripItem[];
  locale: string;
  labels: SrpTopRolesStripLabels;
  /** Show placeholder row while client fetch runs (no SSR seed). */
  isPending?: boolean;
}

const chipClass =
  'group/chip inline-flex min-h-9 max-w-[min(100%,14rem)] shrink-0 items-center gap-2 rounded-full ' +
  'bg-white/88 pl-1 pr-3 py-1 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_14px_rgba(0,0,0,0.05)] ' +
  'ring-1 ring-black/[0.06] backdrop-blur-md transition-[transform,box-shadow] duration-200 ease-out ' +
  'hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_8px_22px_rgba(0,0,0,0.07)] active:scale-[0.97] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF]/40 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-[#F8F5F0] ' +
  'dark:bg-[#2A1D0F]/95 dark:ring-[#4A3520] dark:shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_4px_20px_rgba(0,0,0,0.5)] ' +
  'dark:hover:bg-[#342415]/95 dark:hover:ring-[#5A4126] dark:hover:shadow-[0_1px_0_rgba(255,255,255,0.07)_inset,0_8px_28px_rgba(0,0,0,0.55)] ' +
  'dark:focus-visible:ring-[#98CFA8]/50 dark:focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#170F07]';

const hubLinkClass =
  'shrink-0 rounded-full px-2.5 py-1 text-[12px] sm:text-[13px] font-semibold tracking-tight ' +
  'text-[#007AFF] transition-[color,opacity,transform] duration-200 hover:opacity-80 active:scale-[0.97] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF]/40 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-[#F8F5F0] ' +
  'dark:text-[#98CFA8] dark:hover:text-[#B6D4BC] dark:hover:opacity-100 ' +
  'dark:focus-visible:ring-[#98CFA8]/45 dark:focus-visible:ring-offset-[#170F07]';

/** Compact circular briefcase glyph — roles have no per-item logo, unlike the employers strip. */
function RoleChipIcon(): React.ReactElement {
  return (
    <span
      aria-hidden
      className="pointer-events-none relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-white to-black/[0.04] ring-1 ring-black/[0.07] dark:from-[#3D3529] dark:to-[#1F1509] dark:ring-[#4A3520]"
    >
      <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5 text-black/50 dark:text-[#C4A882]">
        <rect x="3" y="6.5" width="14" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M7.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 12.5 5v1.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M3 10.5h14" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    </span>
  );
}

/**
 * Compact SRP teaser: roles ranked by number of salary-sampled postings (same source as the
 * salary hub's "Most in-demand roles"). Chips sit in one row; when they exceed the width, the
 * row scrolls horizontally (touch + pointer).
 */
export function SrpTopRolesStrip({
  roles,
  locale,
  labels,
  isPending = false,
}: SrpTopRolesStripProps): React.ReactElement | null {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const hasFiredImpressionRef = React.useRef(false);

  React.useEffect(() => {
    hasFiredImpressionRef.current = false;
  }, [roles]);

  React.useEffect(() => {
    if (isPending || roles.length === 0) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasFiredImpressionRef.current) {
            hasFiredImpressionRef.current = true;
            trackEvent({
              name: 'srp_top_roles_strip_interaction',
              params: { action: 'impression', placement: currentStripPlacement(), locale },
            });
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [roles, isPending, locale]);

  if (isPending && roles.length === 0) {
    return (
      <div
        className={
          'w-full min-w-0 rounded-[20px] border border-black/[0.08] bg-white/50 px-3 py-2.5 ' +
          'shadow-[0_1px_1px_rgba(0,0,0,0.04),0_8px_28px_rgba(0,0,0,0.06)] backdrop-blur-2xl supports-[backdrop-filter]:bg-white/42 ' +
          'dark:border-[#4A3520] dark:bg-[#1F1509]/92 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_10px_40px_rgba(0,0,0,0.55)] ' +
          'dark:supports-[backdrop-filter]:bg-[#1F1509]/88 sm:px-4 sm:py-3.5'
        }
        role="status"
        aria-busy="true"
        aria-label={labels.label}
      >
        <div className="flex flex-col gap-2.5">
          <div className="flex items-start justify-between gap-3">
            <span className="h-3 w-28 animate-pulse rounded bg-[#DDD5C8] dark:bg-[#4A3520]" />
            <span className="h-3 w-40 max-w-[45%] animate-pulse rounded bg-[#DDD5C8]/80 dark:bg-[#342415]" />
          </div>
          <div className="scrollbar-hide min-w-0 overflow-x-hidden pb-1">
            <div className="-mx-1 flex w-max flex-nowrap gap-2 px-1">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <span
                  key={i}
                  className="inline-flex h-9 min-w-[6.5rem] shrink-0 animate-pulse rounded-full bg-[#F2EADF]/90 dark:bg-[#2A1D0F]"
                  aria-hidden
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!roles.length) return null;

  return (
    <div
      ref={containerRef}
      className={
        'w-full min-w-0 rounded-[20px] border border-black/[0.08] bg-white/50 px-3 py-2.5 ' +
        'shadow-[0_1px_1px_rgba(0,0,0,0.04),0_8px_28px_rgba(0,0,0,0.06)] backdrop-blur-2xl supports-[backdrop-filter]:bg-white/42 ' +
        'dark:border-[#4A3520] dark:bg-[#1F1509]/92 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_10px_40px_rgba(0,0,0,0.55)] ' +
        'dark:supports-[backdrop-filter]:bg-[#1F1509]/88 sm:px-4 sm:py-3.5'
      }
      role="navigation"
      aria-label={labels.label}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 pt-0.5 text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-black/45 dark:text-[#B8A48C]">
            {labels.label}
          </p>
          <Link
            href={salaryHubUrlPath(locale)}
            locale={locale}
            className={hubLinkClass}
            onClick={() =>
              trackEvent({
                name: 'srp_top_roles_strip_interaction',
                params: { action: 'hub_cta_click', placement: currentStripPlacement(), locale },
              })
            }
          >
            {labels.hubCta}
          </Link>
        </div>

        <div className="scrollbar-hide min-w-0 overflow-x-auto overscroll-x-contain pb-1 touch-pan-x [-webkit-overflow-scrolling:touch]">
          <div className="-mx-1 flex w-max flex-nowrap gap-2 px-1">
            {roles.map((r) => (
              <Link
                key={r.slug}
                href={salaryRoleUrlPath(locale, r.slug)}
                locale={locale}
                className={chipClass}
                title={r.title}
                onClick={() =>
                  trackEvent({
                    name: 'srp_top_roles_strip_interaction',
                    params: { action: 'role_chip_click', placement: currentStripPlacement(), roleSlug: r.slug, locale },
                  })
                }
              >
                <RoleChipIcon />
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight tracking-tight text-black/[0.88] transition-colors group-hover/chip:text-black dark:text-[#F2EADF] dark:group-hover/chip:text-[#F2EADF]">
                    {r.title}
                  </span>
                  {r.sampleSize > 0 ? (
                    <span
                      className="shrink-0 rounded-md bg-black/[0.07] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-black/70 dark:bg-[#4A3520] dark:text-[#C4A882]"
                      aria-label={`${r.sampleSize} postings`}
                    >
                      {r.sampleSize}
                    </span>
                  ) : null}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
